<template>
    <div
        v-if="isOpen"
        v-body-lock="isOpen"
        class="header-mobile-menu"
    >
        <HeaderSearch />
        <p
            v-if="pathToCurrentPage"
            class="header-mobile-menu__path"
        >
            {{ pathToCurrentPage }}
        </p>
        <div
            v-if="!isSubmenuOpen"
            class="header-mobile-menu-content"
        >
            <div :class="['inner-menu', {'inner-menu--shrink': pathToCurrentPage}]">
                <div class="inner-menu-titles flex flex-column">
                    <template v-for="(item, index) in menuMainTitles">
                        <button
                            v-if="item.isActive"
                            :key="`${item.slug}_${index}`"
                            class="inner-menu-titles__item flex items-center justify-between mb-32"
                            @click="menuHandler(item)"
                        >
                            <nuxt-img
                                v-if="item.title.toLowerCase() === 'esg' && item.icon"
                                :src="`icons/${item.icon}`"
                                alt=""
                            />
                            <template v-else>
                                {{ item.title }}
                            </template>
                            <span
                                v-if="item.subMenu && item.subMenu.length"
                                class="inner-menu-titles__item_icon"
                            />
                        </button>
                    </template>
                </div>
            </div>
            <div class="header-mobile-menu_separator" />
            <div class="inner-additional-actions">
                <button
                    v-for="{title, icon, name} in renderMenuActions"
                    :key="name"
                    class="additional-action flex items-center justify-start mb-52"
                    @click="openActionPage(name)"
                >
                    <nuxt-img
                        :src="icon"
                        class="additional-action__icon mr-16"
                        alt=""
                    />
                    <span class="mr-16">{{ title }}</span>
                </button>
            </div>
            <div class="inner-additional-languages pl-6">
                <nuxt-img
                    src="icons/flag.svg"
                    class="additional-action__icon mr-16"
                    alt="flag"
                />
                <button
                    v-for="{title, name} in menuLanguages"
                    :key="name"
                    class="additional-languages"
                    @click="openActionPage(name)"
                >
                    <span>{{ title }}</span>
                </button>
            </div>
            <UserRegionsList
                :is-active="isRegionsSelectActive"
                class="user-regions-list--mobile"
                @select="setSelectedRegion"
            />
        </div>
        <div
            v-else
            class="header-mobile-menu-sub"
        >
            <button
                class="prev-btn mt-24 mb-24"
                @click="getPrevMenuItems"
            >
                <span class="prev-btn__icon pr-16" />
                {{ prevBtnTitle }}
            </button>
            <div class="header-mobile-menu_separator" />
            <div class="inner-menu">
                <div class="inner-menu-titles flex flex-column">
                    <template v-for="(item, index) in newSubMenu">
                        <button
                            v-if="item.isActive"
                            :key="`${item.slug}_inner${index}`"
                            class="inner-menu-titles__item flex items-center justify-between mb-32"
                            :disabled="isSameRoute(item)"
                            @click="menuHandler(item)"
                        >
                            {{ item.title }}
                            <span
                                v-if="item.subMenu && item.subMenu.length"
                                class="inner-menu-titles__item_icon"
                            />
                        </button>
                    </template>
                    <template v-if="otherMenuItems && otherMenuItems.length">
                        <template v-for="(item, index) in otherMenuItems">
                            <button
                                v-if="item.isActive"
                                :key="`${item.slug}_inner${index}`"
                                :disabled="isSameRoute(item)"
                                class="inner-menu-titles__item flex items-center justify-between mb-32"
                                @click="menuHandler(item)"
                            >
                                {{ item.title }}
                                <span
                                    v-if="item.subMenu && item.subMenu.length"
                                    class="inner-menu-titles__item_icon"
                                />
                            </button>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import path from 'path';
import {computed, defineComponent, ref, watch} from 'vue';
import {useContext, useRoute, useRouter} from '@nuxtjs/composition-api';
import clone from 'lodash.clonedeep';

import {useRegionsStore} from '@/stores/regions';
import {useNavigationStore} from '@/stores/navigation';
import {usePopupsStore} from '@/stores/popups';
import {getMainItems} from '@/core/utils/menu';
import {actions, languages} from '@/core/models/common/HeaderMobileMenuModel';
import {DEFAULT_PARAMS, USER_LOCATION} from '@/core/constants/cookies';
import bodyLock from '@/core/directives/bodyLock';
import HeaderSearch from '@/components/Sections/Header/HeaderSearch.vue';
import UserRegionsList from '@/components/UserRegions/UserRegionsList.vue';

export default defineComponent({
    name: 'HeaderMobileMenu',
    components: {
        HeaderSearch,
        UserRegionsList,
    },
    directives: {
        'body-lock': bodyLock,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    setup(props, context) {
        const {$cookies, $fetchVisitorLead} = useContext();
        const route = useRoute();
        const router = useRouter();

        const regionsStore = useRegionsStore();
        const navigationStore = useNavigationStore();

        const {setUserRegion} = regionsStore;

        const activeRegion = computed(() => regionsStore.activeRegion);
        const menu = computed(() => navigationStore.menu);

        const popupsStore = usePopupsStore();
        const {setIsPopupShown} = popupsStore;

        const isSubmenuOpen = ref(false);
        const newSubMenu = ref([]);
        const menuStack = ref([]);
        const pathToPage = ref([]);
        const activeMainItem = ref([]);
        const menuActions = ref(clone(actions));
        const menuLanguages = ref(clone(languages));
        const isRegionsSelectActive = ref(false);

        const renderMenuActions = computed(() => {
            return menuActions.value.map((action) => {
                if (action.name === 'location') {
                    return {...action, title: activeRegion.value.name};
                };

                return action;
            });
        });

        const menuMainTitles = computed(() => {
            return menu.value?.filter((item) => {
                return item.isActive && !item.isOther;
            }) ?? [];
        });

        const prevBtnTitle = computed(() => {
            if (menuStack.value.length <= 2) {
                return 'Назад';
            }

            return menuStack.value.slice(-2)[0][0]?.title ?? 'Назад';
        });

        const otherMenuItems = computed(() => {
            const currentMenuItem = menu.value?.find((item) => item.title === activeMainItem.value.slice(-1)[0]) ?? [];

            return currentMenuItem?.subMenu?.filter((item) => {
                return item?.other;
            }) ?? [];
        });

        const pathToCurrentPage = computed(() => {
            const targetSlug = route.value.path?.split('/').filter(Boolean).slice(-1)[0] || route.value.name;
            const result = findMenuItem(menu.value, targetSlug);

            return result?.path;
        });

        const currentPath = computed(() => {
            return route.value.path.endsWith('/') ? route.value.path.slice(0, -1) : route.value.path;
        });

        watch(() => props.isOpen, (newVal, oldVal) => {
            if (!newVal || newVal === oldVal) {
                return;
            }

            isSubmenuOpen.value = false;
            newSubMenu.value = [];
            pathToPage.value = [];
            menuStack.value = [];
            setIsPopupShown(false);
        }, {immediate: true});

        watch(menuStack, (newVal) => {
            if (!newVal.length) {
                isSubmenuOpen.value = false;
            }
        }, {deep: true});

        function openActionPage(name) {
            switch (name) {
                case 'offices':
                    router.push('/about/offices-and-atms');
                    break;
                case 'location':
                    isRegionsSelectActive.value = !isRegionsSelectActive.value;
                    break;
                case 'currency-rates':
                    router.push('/for-individuals/currency-rates');
                    break;
                case 'en':
                    window.location.href = 'https://centrinvest.com/';
                    break;
                case 'zh':
                    window.location.href = 'https://centrinvest.com/cn';
                    break;
                default:
                    break;
            }
        }

        function getPrevMenuItems() {
            const prevItem = menuStack.value.splice(-2)[0] ?? [];
            newSubMenu.value = prevItem;

            if (JSON.stringify(prevItem) !== JSON.stringify(menuMainTitles.value)) {
                menuStack.value.push(prevItem);
            }

            const pathToPageLength = menuStack.value.length - 1;
            pathToPage.value.length = pathToPageLength > 0 ? pathToPageLength : 0;
            activeMainItem.value.pop();
        }

        function redirectToPage(pathToPage) {
            router.push(pathToPage);
        }

        function getLink(item) {
            return item?.path ?? path.normalize(`/${pathToPage.value.join('/')}`);
        }

        function isSameRoute(item) {
            if (!item.slug) {
                return false;
            }

            return currentPath.value.endsWith(item.slug);
        }

        function menuHandler(item) {
            if (item.isExternal) {
                window.open(item.path, '_blank');

                return;
            }

            if (activeMainItem.value[activeMainItem.value.length - 1] !== item.title) {
                activeMainItem.value.push(item.title);
            }

            if (!item.slug && !item.isExpanded && !item.isExternal) {
                redirectToPage(item.path);

                return;
            }

            if (!pathToPage.value.includes(item.slug)) {
                if (pathToPage.value.length > 2) {
                    pathToPage.value.pop();
                    activeMainItem.value.pop();
                }

                pathToPage.value.push(item.slug);
            }

            isSubmenuOpen.value = true;

            if (!menuStack.value.length) {
                menuStack.value.push(menuMainTitles.value);
            }

            if (item.subMenu) {
                newSubMenu.value = getNewSubmenu(item);
                menuStack.value.push(newSubMenu.value);

                return;
            }

            if (getLink(item) === currentPath.value) {
                return;
            }

            redirectToPage(getLink(item));
        }

        function getNewSubmenu(item) {
            const mainItem = clone(item);
            delete mainItem.subMenu;

            return [mainItem].concat(getMainItems(item.subMenu));
        }

        function setSelectedRegion(region) {
            $cookies.set(USER_LOCATION, JSON.stringify(region), DEFAULT_PARAMS);
            setUserRegion(region);
            isRegionsSelectActive.value = !isRegionsSelectActive.value;
            $fetchVisitorLead(true);
        }

        function findMenuItem(menu, targetSlug, currentPath = []) {
            for (const menuItem of menu) {
                const path = currentPath.concat(menuItem.title);

                if (menuItem.slug === targetSlug) {
                    return {
                        menuItem,
                        path: path.join(' / '),
                    };
                }

                if (menuItem.subMenu) {
                    const result = findMenuItem(menuItem.subMenu, targetSlug, path);
                    if (result) {
                        return result;
                    }
                }
            }

            return null;
        }

        return {
            isSubmenuOpen,
            newSubMenu,
            menuLanguages,
            isRegionsSelectActive,
            renderMenuActions,
            menuMainTitles,
            prevBtnTitle,
            otherMenuItems,
            pathToCurrentPage,
            openActionPage,
            getPrevMenuItems,
            menuHandler,
            setSelectedRegion,
            isSameRoute,
        };
    },
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/common/header/header-mobile';
</style>
